import { COLORS, STATUS } from '@btime/constants'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import FlexGrid, { FlexCol } from '../../structure/FlexGrid'
import { SmallText } from '../../../packages/kiti/components/presentation/Text'
import StockItemSelect from '../../../containers/stocks/select/StockItemSelect'
import OperationType from '../../../containers/stocks/select/OperationType'
import NumberInput from '../NumberInput'
import { genClientCode } from '../../../modules/stocks/_helper/helper'
import { useLoggedUser } from '@btime/react-session'
import { useExecutionForm } from '../../../modules/serviceOrders/executionV2/_form/ExecutionFormHandler'
import PlaceSelect from '../../../containers/places/select/PlaceSelect'

const StockInput = ({ onChange, value, stockOperationType, allowOnlyQrCode }) => {
  const form = useExecutionForm()
  const loggedUser = useLoggedUser() || {}
  const [item, setItem] = useState(value?.item || {})
  const [place, setPlace] = useState(value?.place || {})
  const [amount, setAmount] = useState(value?.amount)
  const [operationType, setOperationType] = useState(value?.operationType || stockOperationType || {})

  const disabled =
    [STATUS.COMPLETED, STATUS.APPROVED, STATUS.CANCELED, STATUS.REFUSED, STATUS.UNPRODUCTIVE].includes(
      form?.currentStatus
    ) || allowOnlyQrCode

  const clientCode = useMemo(() => {
    if (value?.clientCode) {
      return value.clientCode
    }
    return genClientCode()
  }, [value])

  useEffect(() => {
    if (!item) return onChange && onChange({})

    const isAllFieldsFilled =
      !!item?.id &&
      !!operationType?.id &&
      !!amount &&
      amount > 0 &&
      (['transfer_from_general_to_place', 'transfer_from_place_to_general'].includes(operationType?.id)
        ? !!place?.id
        : true)

    if (!isAllFieldsFilled) {
      setAmount(undefined)
      onChange && onChange(undefined)
      return
    }

    let value = { clientCode, item, amount, operationType }
    if (operationType?.id === 'add_to_general') {
      value = {
        ...value,
        operationType,
        fromUserId: null,
        toUserId: null
      }
    }
    if (operationType?.id === 'transfer_from_general_to_user') {
      value = {
        ...value,
        toUserId: loggedUser.id,
        fromUserId: null
      }
    }
    if (operationType?.id === 'transfer_from_user_to_general') {
      value = {
        ...value,
        fromUserId: loggedUser.id,
        toUserId: null
      }
    }
    if (operationType?.id === 'self_use') {
      value = {
        ...value,
        fromUserId: loggedUser.id,
        toUserId: null
      }
    }
    if (operationType?.id === 'remove_general_stock') {
      value = {
        clientCode,
        item,
        amount,
        operationType,
        fromUserId: null,
        toUserId: null
      }
    }
    if (operationType?.id === 'transfer_from_place_to_general') {
      value = {
        clientCode,
        item,
        amount,
        operationType,
        fromPlaceId: place?.id,
        toPlaceId: null
      }
    }
    if (operationType?.id === 'transfer_from_general_to_place') {
      value = {
        clientCode,
        item,
        amount,
        operationType,
        fromPlaceId: null,
        toPlaceId: place?.id
      }
    }

    onChange && onChange(value)
  }, [item.id, place.id, operationType.id, amount, loggedUser.id])

  const handleChangeItem = useCallback(
    value => {
      if (value) {
        setItem(value)
      } else {
        setItem({})
        setAmount()
        if (!stockOperationType) {
          setOperationType({})
        }
      }
    },
    [stockOperationType]
  )

  const handleChangePlace = useCallback(
    value => {
      if (value) {
        setPlace(value)
      } else {
        setPlace({})
        setAmount()
        if (!stockOperationType) {
          setOperationType({})
        }
      }
    },
    [stockOperationType]
  )

  const handleChangeAmount = useCallback(value => {
    setAmount(value)
  }, [])

  const handleChangeOperationType = useCallback((value = {}) => {
    setOperationType(value)
  }, [])

  return (
    <FlexGrid key={clientCode} gutter={0.5} justify="flex-start" align="flex-start">
      <FlexCol flex={0.5}>
        <SmallText size="xsmall" color={COLORS.dark3}>
          Item
        </SmallText>
        <StockItemSelect value={item} onChange={handleChangeItem} disabled={disabled} />
      </FlexCol>
      <FlexCol flex={0.5}>
        <SmallText size="xsmall" color={COLORS.dark3}>
          Tipo da Operação
        </SmallText>
        <OperationType
          value={operationType}
          onChange={handleChangeOperationType}
          disabled={disabled || stockOperationType}
        />
      </FlexCol>
      {['transfer_from_place_to_general', 'transfer_from_general_to_place'].includes(operationType?.id) && (
        <FlexCol flex={0.5}>
          <SmallText size="xsmall" color={COLORS.dark3}>
            Local
          </SmallText>
          <PlaceSelect value={place} onChange={handleChangePlace} disabled={disabled} />
        </FlexCol>
      )}
      <FlexCol flex={0.5}>
        <SmallText size="xsmall" color={COLORS.dark3}>
          Quantidade
        </SmallText>
        <NumberInput value={amount} onStopTyping={handleChangeAmount} disabled={disabled} keepValueWhenDisabled />
      </FlexCol>
    </FlexGrid>
  )
}

export default StockInput
